import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, User, ArrowLeft, Share2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BreathRegeneration = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate('/blog');
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={handleGoBack}
            className="mb-8 -ml-4 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>
          
          <div className="mb-8">
            <Badge className="mb-4">Wellness Practices</Badge>
            <h1 className="text-4xl md:text-6xl font-serif font-light mb-6 text-foreground leading-tight">
              Breath – The Forgotten Tool of Regeneration
            </h1>
            
            <div className="flex items-center gap-6 text-sm text-muted-foreground mb-8">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>June 21, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>8 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>Piotr</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm">
                <Share2 className="w-4 h-4 mr-2" />
                Share Article
              </Button>
            </div>
          </div>
          
          <div className="relative overflow-hidden rounded-2xl mb-12">
            <img 
              src="/lovable-uploads/1d5b7aa6-1cf8-434e-b7b2-cd7f5630d998.png" 
              alt="Calm water surface with breathe text overlay representing mindful breathing"
              className="w-full h-[400px] md:h-[500px] object-cover object-center"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="px-6 pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <div className="text-xl leading-relaxed text-muted-foreground mb-8 font-light italic">
              Right now, as you read this, you are breathing. Yet how often do you notice it? Breath is our most constant companion – we take around 20,000 breaths a day – and still, it remains one of the most overlooked tools for health and balance. In a world where stress and distraction dominate, conscious breathing offers a simple, accessible way to restore calm and energy.
            </div>
            
            <div className="space-y-8 text-lg leading-relaxed text-foreground">
              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The science of breath</h2>

              <p>
                Breathing is more than a mechanical process. It directly influences our nervous system. Slow, deep breaths stimulate the vagus nerve, which activates the parasympathetic system – our body's natural "rest and digest" mode. Research shows that controlled breathing can lower blood pressure, reduce anxiety, and improve emotional regulation (Jerath et al., 2006).
              </p>

              <p>
                A study published in <em>Frontiers in Human Neuroscience</em> found that regular practice of slow breathing significantly <strong className="text-accent">increases heart rate variability (HRV)</strong> – a marker of resilience to stress (Lehrer & Gevirtz, 2014). Another review highlighted that breath-based practices improve sleep quality, reduce symptoms of depression, and support overall mental health (Zaccaro et al., 2018).
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Breath in ancient traditions</h2>

              <p>
                Across cultures, breath has always been central to well-being. In yoga, <em>pranayama</em> refers to breath control as a pathway to clarity and vitality. In Buddhist practice, awareness of breath anchors meditation. Even in Western traditions, phrases like "take a deep breath" reflect an intuitive understanding that breath restores balance.
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Practical exercises</h2>

              <p>
                You don't need special equipment to practice conscious breathing. Here are a few techniques backed by research:
              </p>

              <ul className="space-y-3 ml-6">
                <li><strong className="text-accent">Box Breathing (4-4-4-4)</strong>: Inhale for 4 counts, hold for 4, exhale for 4, hold for 4. Used by athletes and even the military to calm nerves.</li>
                <li><strong className="text-accent">Coherent Breathing (5-6 breaths per minute)</strong>: Slow, steady breaths to regulate HRV and relax the nervous system.</li>
                <li><strong className="text-accent">Extended Exhale</strong>: Inhale through the nose, then exhale longer than you inhale (e.g., 4 in, 6 out). Effective for reducing anxiety.</li>
              </ul>

              <p>
                Just 5–10 minutes a day of focused breathing can make a noticeable difference in mood and energy.
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Conclusion</h2>

              <p>
                Breath is always with us – free, accessible, and profoundly healing. In rediscovering the power of breathing, we rediscover a direct line to calm, clarity, and resilience. Sometimes, the most powerful medicine is as simple as taking a deep breath.
              </p>

              <p className="text-xl font-light italic text-accent">
                In every breath lies the potential for renewal.
              </p>

              <div className="bg-card p-8 rounded-2xl mt-16 border border-border/20">
                <h3 className="text-2xl font-serif font-light mb-4 text-foreground">References</h3>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>• Jerath, R., Edry, J. W., Barnes, V. A., & Jerath, V. (2006). Physiology of long pranayamic breathing: Neural respiratory elements may provide a mechanism that explains how slow deep breathing shifts the autonomic nervous system. <em>Medical Hypotheses, 67</em>(3), 566–571.</p>
                  <p>• Lehrer, P. M., & Gevirtz, R. (2014). Heart rate variability biofeedback: How and why does it work? <em>Frontiers in Psychology, 5</em>, 756.</p>
                  <p>• Zaccaro, A., Piarulli, A., Laurino, M., Garbella, E., Menicucci, D., Neri, B., & Gemignani, A. (2018). How breath-control can change your life: A systematic review on psycho-physiological correlates of slow breathing. <em>Frontiers in Human Neuroscience, 12</em>, 353.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BreathRegeneration;